import { Box, BoxProps, Collapse, Flex, IconButton, Text, Tooltip, useStyleConfig } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron_down.svg';
import { ReactComponent as ChevronUpIcon } from 'assets/icons/chevron_up.svg';
import { ReactElement, ReactNode, useCallback, useState } from 'react';

interface CollapsibleBoxProps extends BoxProps {
  title: string;
  collapseAriaLabel: string;
  children: ReactNode;
  titleIcon?: ReactElement;
  variant?: string;
  isOpenOnMount?: boolean;
  tooltipLabel?: string;

  // Element to render on the right of the title header
  rightElement?: ReactElement | null;

  // Whether to show the chevron toggle button
  // If toggle is not shown, clicking the title will toggle the box
  showToggle?: boolean;

  // Triggered when box is opened manually
  onOpen?: () => void;
}

const StyledIconButton = styled(IconButton)`
  &:hover {
    background-color: transparent;
  }
`;

const OutlinedTooltipButton = ({
  isOpen,
  title,
  titleIcon,
  tooltipLabel,
  onToggleCollapse,
}: {
  title: string;
  tooltipLabel: string;
  isOpen: boolean;
  titleIcon?: ReactElement;
  onToggleCollapse: () => void;
}) => (
  <Flex alignItems={'center'} justifyContent={'space-between'}>
    <Tooltip hasArrow label={tooltipLabel} isDisabled={!tooltipLabel}>
      <Flex
        gap={2}
        alignItems={'center'}
        as={'button'}
        onClick={onToggleCollapse}
        h={7}
        py={2.5}
        px={2}
        border={'1px solid'}
        borderColor={'gray.200'}
        borderRadius={'base'}
        aria-label={title}
        _hover={{
          bg: 'blackAlpha.200',
        }}
        transition='0.2s'
      >
        {titleIcon}
        <Text fontWeight={500} fontSize={'xs'} color={'font.500'} as='span'>
          {title}
        </Text>
        {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Flex>
    </Tooltip>
  </Flex>
);

/**
 * COLLAPSIBLE BOX COMPONENT
 *
 * Renders a box that can be expanded/collapsed.
 * When collapsed, only displays [title].
 * When expanded, will display [title] and [children].
 *
 * Note: In the future, can apply variants for different color themed boxes.
 * For now, just renders a blue info box - for example, can add error (red), warn (yellow),
 * success (green) themed boxes.
 */
export const CollapsibleBox = ({
  title,
  titleIcon,
  collapseAriaLabel,
  children,
  variant,
  isOpenOnMount,
  rightElement,
  showToggle = true,
  onOpen,
  tooltipLabel = '',
  ...boxProps
}: CollapsibleBoxProps) => {
  const styles = useStyleConfig('CollapsibleBox', { variant });
  const [isOpen, setOpen] = useState(isOpenOnMount);

  const toggleCollapse = useCallback(() => {
    // Trigger on open callback if box is being opened
    if (!isOpen && onOpen) onOpen();

    setOpen(!isOpen);
  }, [isOpen, onOpen]);

  const titleProps = showToggle
    ? null
    : {
        onClick: toggleCollapse,
        p: 2,
        borderRadius: 'base',
        _hover: {
          bg: 'hover.gray',
        },
        transition: '0.2s',
      };

  return (
    <Box __css={styles} {...boxProps}>
      <Flex alignItems={'center'} justifyContent={'space-between'}>
        {tooltipLabel ? (
          <OutlinedTooltipButton
            isOpen={Boolean(isOpen)}
            title={title}
            titleIcon={titleIcon}
            tooltipLabel={tooltipLabel}
            onToggleCollapse={toggleCollapse}
          />
        ) : (
          <>
            <Flex gap={2} alignItems={'center'} as={!showToggle ? 'button' : 'div'} {...titleProps}>
              {titleIcon}
              <Text fontWeight={700} fontSize={'xs'}>
                {title}
              </Text>
            </Flex>
          </>
        )}
        <Box>
          {showToggle && (
            <StyledIconButton
              aria-label={collapseAriaLabel}
              bg={'transparent'}
              size={'xs'}
              icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              onClick={toggleCollapse}
            />
          )}
          {rightElement}
        </Box>
      </Flex>
      <Collapse in={isOpen}>{children}</Collapse>
    </Box>
  );
};
